import {
  AppUser,
  FilteredItineraryEntry,
  ICategory,
  ItineraryFilter,
  ItineraryFilterOptions,
  TActivityType,
  TimeFilter,
  V2,
} from "@eventr/common";
import { FilterSelection, FilterTarget } from "./filterTargets.interface";

/**
 * Converts a target's user selection into options for `ItineraryFilter`.
 *
 * This is the single place the app translates "what the user toggled" into the common
 * filtering engine's vocabulary, so notifications, calendar, and widgets all derive their
 * entry sets the same way.
 *
 * @param selection     The user's exclude-set for one itinerary/target.
 * @param target        Drives time scope (calendar and map cover past + future; notifications and
 *                      widgets only upcoming) and, for the map, restricts to located entries.
 * @param orgCategories The org's custom categories, used to resolve excluded `pub_id`s to `ICategory`.
 */
/**
 * Per-target filtering policy — the single declarative source for how the targets differ. Adding a
 * target is a new row here, not a new conditional. (The map additionally overrides `timeFilter` at
 * its own call site from a UI control.)
 */
interface TargetPolicy {
  /** Forward-looking feed (`"upcoming"`) vs whole-trip view (`"all"`). */
  timeFilter: TimeFilter;
  /** Only the map plots located entries. */
  hasLocation: boolean;
  /** Only the up-next widget feed restricts to sections flagged `displayInUpNext`. */
  requireDisplayInUpNext: boolean;
  /** Sync only the current user's own entries (notifications/widgets/calendar); the map shows the whole trip. */
  userVisibility: boolean;
  /** The up-next feed never surfaces todos/stands and hides unconfirmed (pending) entries. */
  excludeUpNextNoise: boolean;
}

const TARGET_POLICY: Record<FilterTarget, TargetPolicy> = {
  notifications: { timeFilter: "upcoming", hasLocation: false, requireDisplayInUpNext: true, userVisibility: true, excludeUpNextNoise: true },
  widgets: { timeFilter: "upcoming", hasLocation: false, requireDisplayInUpNext: true, userVisibility: true, excludeUpNextNoise: true },
  calendar: { timeFilter: "all", hasLocation: false, requireDisplayInUpNext: false, userVisibility: true, excludeUpNextNoise: false },
  map: { timeFilter: "all", hasLocation: true, requireDisplayInUpNext: false, userVisibility: false, excludeUpNextNoise: false },
};

export function toItineraryFilterOptions(
  selection: FilterSelection,
  target: FilterTarget,
  orgCategories: ICategory[],
  user?: AppUser,
): ItineraryFilterOptions {
  const policy = TARGET_POLICY[target];

  return {
    excludeSections: selection.excludedSections,
    excludeItemsWithCustomCategories: orgCategories.filter((category) =>
      selection.excludedCategories.includes(category.pub_id),
    ),
    timeFilter: policy.timeFilter,
    hasLocation: policy.hasLocation,
    removeDuplicateEntries: true,
    requireDisplayInUpNext: policy.requireDisplayInUpNext,
    userVisibility:
      policy.userVisibility && user
        ? { selectedUser: user, currentUser: user, includePublic: selection.showPublic }
        : undefined,
    ...(policy.excludeUpNextNoise ? { excludeSubtypes: ["todo", "stand"] as TActivityType[], excludePending: true } : {}),
  };
}

/**
 * Builds the filtered entry set for an itinerary/target — the single place the app turns a stored
 * selection into `ItineraryFilter` results, so every target (notifications, calendar, …) derives its
 * entries identically.
 */
export function filteredEntriesForTarget(
  sections: V2[] | undefined,
  selection: FilterSelection,
  target: FilterTarget,
  orgCategories: ICategory[],
  user?: AppUser,
): FilteredItineraryEntry[] {
  return new ItineraryFilter(
    sections ?? [],
    toItineraryFilterOptions(selection, target, orgCategories, user),
  ).getEntries();
}

/** The org categories actually referenced by a set of filtered entries (for the per-itinerary toggle list). */
export function extractUsedCategories(entries: FilteredItineraryEntry[], orgCategories: ICategory[]): ICategory[] {
  const usedCategoryIds = new Set<string>();
  for (const entry of entries) {
    const entryCategories = (entry.component as { categories?: unknown }).categories;
    if (Array.isArray(entryCategories)) {
      for (const id of entryCategories) usedCategoryIds.add(String(id));
    }
  }
  return orgCategories.filter((category) => usedCategoryIds.has(category.pub_id));
}
